import React from "react";
import axios from "axios";
import { formatUsPhone, maskUsPhone } from 'utilities/formatPhone';
import { useNavigate } from "react-router-dom";
import { handleApiResponse, handleAxiosError } from 'utilities/apiUtils';
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import '../../../styles/panels.css';

declare global {
  interface Window {
    google: any;
  }
}

const INTEREST_TYPES = [
  { key: "additionalInsured", label: "Additional insured" },
  { key: "lossPayee", label: "Loss payee" },
  { key: "breachOfWarranty", label: "Breach of warranty" },
  { key: "mortgagee", label: "Mortgagee" },
  { key: "coOwner", label: "Co-owner" },
  { key: "owner", label: "Owner" },
  { key: "leasebackOwner", label: "Leaseback owner" },
  { key: "registrant", label: "Registrant" },
  { key: "lienholder", label: "Lienholder" },
  { key: "trustee", label: "Trustee" },
  { key: "obligee", label: "Obligee" },
  { key: "other", label: "Other" },
];

export type AddAdditionalInterestDialogProps = {
  visible: boolean;
  onHide: () => void;
  onSave: (data: any) => void;
  insuredDatabaseId?: string;
  insuredEmail?: string;
  insuredFirstName?: string;
  insuredLastName?: string;
  insuredCommercialName?: string;
};

export default function AddAdditionalInterestDialog({
  visible,
  onHide,
  onSave,
  insuredDatabaseId,
  insuredEmail,
  insuredFirstName,
  insuredLastName,
  insuredCommercialName,
}: AddAdditionalInterestDialogProps) {
  const [companyName, setCompanyName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [cellPhone, setCellPhone] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [fax, setFax] = React.useState("");
  const [address1, setAddress1] = React.useState("");
  const [address2, setAddress2] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [zip, setZip] = React.useState("");
  const [addressSuggestions, setAddressSuggestions] = React.useState<any[]>([]);
  const [addressLoading, setAddressLoading] = React.useState(false);
  const address1Ref = React.useRef<HTMLInputElement>(null);
  const address2Ref = React.useRef<HTMLInputElement>(null);
  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

  const handleAddress1Change = React.useCallback((value: string) => {
    setAddress1(value);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    if (value.length > 2 && window.google && window.google.maps && window.google.maps.places) {
      setAddressLoading(true);
      debounceTimeout.current = setTimeout(() => {
        const autocompleteService = new window.google.maps.places.AutocompleteService();
        autocompleteService.getPlacePredictions(
          { input: value, types: ['address'] },
          (predictions: any[]) => {
            setAddressSuggestions(predictions || []);
            setAddressLoading(false);
          }
        );
      }, 300);
    } else {
      setAddressSuggestions([]);
      setAddressLoading(false);
    }
  }, []);

  const selectAddressSuggestion = React.useCallback((prediction: any) => {
    setAddress1(prediction.description);
    setAddressSuggestions([]);
    setAddressLoading(false);
    if (window.google && window.google.maps && window.google.maps.places) {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      placesService.getDetails({ placeId: prediction.place_id }, (place: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          fillAddressFields(place);
        }
      });
    }
  }, []);

  function fillAddressFields(place: any) {
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zip = '';

    if (place.address_components) {
      place.address_components.forEach((component: any) => {
        const types = component.types;
        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          route = component.long_name;
        }
        if (types.includes('locality') || types.includes('administrative_area_level_3')) {
          city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        }
        if (types.includes('postal_code')) {
          zip = component.long_name;
        }
      });
    }

    setAddress1(place.formatted_address || `${streetNumber} ${route}`.trim());
    setCity(city);
    setState(state);
    setZip(zip);
  }
  const [doNotDisplay, setDoNotDisplay] = React.useState(false);
  const [interestTypes, setInterestTypes] = React.useState<string[]>([]);
  const [otherText, setOtherText] = React.useState("");
  const [loanNumber, setLoanNumber] = React.useState("");
  const [selectedHolder, setSelectedHolder] = React.useState<{label: string;value: string;} | null>(null);
  const [holderSuggestions, setHolderSuggestions] = React.useState<{ label: string; value: string }[]>([]);
  const [loadingHolder, setLoadingHolder] = React.useState(false);
  const [profLiability, setProfLiability] = React.useState("");
  const [profLiabilityOptions, setProfLiabilityOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [profLiabilityLoading, setProfLiabilityLoading] = React.useState(false);
  const [policies, setPolicies] = React.useState("");
  const [policiesOptions, setPoliciesOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [policiesLoading, setPoliciesLoading] = React.useState(false);
  const [buildings, setBuildings] = React.useState("");
  const [buildingsOptions, setBuildingsOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [buildingsLoading, setBuildingsLoading] = React.useState(false);
  const [descTemplate, setDescTemplate] = React.useState("");
  const [descText, setDescText] = React.useState("");
  const [descTemplateOptions, setDescTemplateOptions] = React.useState<{ label: string; value: string; body: string }[]>([]);
  const [descTemplatesLoading, setDescTemplatesLoading] = React.useState(false);
  const [acord101, setAcord101] = React.useState("");
  const [certInterest, setCertInterest] = React.useState<any[]>([
    { type: "General liability", addl: false, subr: false },
    { type: "Automobile liability", addl: false, subr: false },
    { type: "Umbrella liability", addl: false, subr: false },
    { type: "Workers' Compensation", addl: false, subr: false },
    { type: "Other liability 1", addl: false, subr: false },
    { type: "Other liability 2", addl: false, subr: false },
    { type: "Garage liability", addl: false, subr: false },
    { type: "Garage keepers liability", addl: false, subr: false },
    { type: "Cargo", addl: false, subr: false },
    { type: "Trailer interchange", addl: false, subr: false },
    { type: "Airport FBO", addl: false, subr: false },
    { type: "Private hangar", addl: false, subr: false },
    { type: "Aviation products", addl: false, subr: false },
    { type: "Aircraft", addl: false, subr: false },
    { type: "Vehicle or equipment", addl: false, subr: false },
    { type: "Insurance binder", addl: false, subr: false },
  ]);
  const holderMap = React.useRef<{ [key: string]: any }>({});

  React.useEffect(() => {
    if (!visible) return;
    if (!window.google || !window.google.maps) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      script.onload = () => {
      };
    }
  }, [visible]);

  const navigate = useNavigate();

  React.useEffect(() => {
    if (!visible) return;
    const fetchProfLiability = async () => {
      setProfLiabilityLoading(true);
      try {
        const NCToken = localStorage.getItem("NCToken");
        const igtoken = localStorage.getItem("igtoken");
        const response = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/professional-liability?top=20`,
          {
            headers: {
              NCToken: NCToken || "",
              Authorization: `Bearer ${igtoken || ""}`,
              Accept: "application/json",
            },
          },
        );
        await handleApiResponse(response, navigate);
        const data = await response.json();
        if (Array.isArray(data.value)) {
          setProfLiabilityOptions(
            data.value.map((item: any) => ({
              label:
                item.firstName && item.lastName
                  ? `${item.firstName} ${item.lastName}`
                  : item.firstName ||
                    item.lastName ||
                    item.title ||
                    item.databaseId,
              value: item.databaseId,
            })),
          );
        } else {
          setProfLiabilityOptions([]);
        }
      } catch (e) {
        setProfLiabilityOptions([]);
      } finally {
        setProfLiabilityLoading(false);
      }
    };
    fetchProfLiability();
  }, [visible]);

  React.useEffect(() => {
    if (!visible) return;
    if (!insuredDatabaseId) {
      setPoliciesOptions([]);
      setPoliciesLoading(false);
      return;
    }
    let cancelled = false;
    const fetchPolicies = async () => {
      setPoliciesLoading(true);
      try {
        let res;
        try {
          res = await axios.post(
            `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insured-policies`,
            { insuredId: insuredDatabaseId },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
                "Content-Type": "application/json",
                NCToken: localStorage.getItem("NCToken") || "",
              },
            },
          );
        } catch (err) {
          handleAxiosError(err, navigate);
          return;
        }
        if (cancelled || !res) return;
        const raw: any[] = Array.isArray(res.data) ? res.data : [];
        const options = raw
          .filter((p: any) => !p.isQuote)
          .map((p: any) => ({
            label: p.policyNumber || p.databaseId || "Policy",
            value: p.databaseId || p.policyNumber,
          }));
        setPoliciesOptions(options);
      } catch {
        if (!cancelled) setPoliciesOptions([]);
      } finally {
        if (!cancelled) setPoliciesLoading(false);
      }
    };
    fetchPolicies();
    return () => {
      cancelled = true;
    };
  }, [visible, insuredDatabaseId]);

  React.useEffect(() => {
    if (!visible) return;
    const fetchBuildings = async () => {
      setBuildingsLoading(true);
      try {
        const NCToken = localStorage.getItem("NCToken");
        const igtoken = localStorage.getItem("igtoken");
        let url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/insured-properties?top=10`;
        if (insuredDatabaseId) {
          url += `&insuredDatabaseId=${encodeURIComponent(insuredDatabaseId)}`;
        }
        const response = await fetch(
          url,
          {
            headers: {
              NCToken: NCToken || "",
              Authorization: `Bearer ${igtoken || ""}`,
              Accept: "application/json",
            },
          },
        );
        await handleApiResponse(response, navigate);
        const data = await response.json();
        if (Array.isArray(data.value)) {
          setBuildingsOptions(
            data.value.map((item: any) => ({
              label:
                item.addressLine1 && item.city
                  ? `${item.addressLine1}, ${item.city}`
                  : item.addressLine1 || item.city || item.databaseId,
              value: item.databaseId,
            })),
          );
        } else {
          setBuildingsOptions([]);
        }
      } catch (e) {
        setBuildingsOptions([]);
      } finally {
        setBuildingsLoading(false);
      }
    };
    fetchBuildings();
  }, [visible, insuredDatabaseId]);

  React.useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const fetchDescTemplates = async () => {
      setDescTemplatesLoading(true);
      try {
        const NCToken = localStorage.getItem('NCToken') || '';
        const igtoken = localStorage.getItem('igtoken') || '';
        const response = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-templates?top=100&category=${encodeURIComponent('Certificates')}`,
          { headers: { NCToken, Authorization: `Bearer ${igtoken}`, Accept: 'application/json' } }
        );
        if (cancelled) return;
        const data = await response.json();
        if (Array.isArray(data.value)) {
          setDescTemplateOptions(data.value.map((item: any) => ({
            label: item.name || item.title || item.id,
            value: item.id,
            body: item.body || '',
          })));
        }
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setDescTemplatesLoading(false);
      }
    };
    fetchDescTemplates();
    return () => { cancelled = true; };
  }, [visible]);

  const completeHolder = async (event: any) => {
    setLoadingHolder(true);
    try {
      const query = event.query;
      const NCToken = localStorage.getItem("NCToken");
      const igtoken = localStorage.getItem("igtoken");
      const response = await fetch(
        `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-holders?top=10&search=${encodeURIComponent(query)}`,
        {
          headers: {
            NCToken: NCToken || "",
            Authorization: `Bearer ${igtoken || ""}`,
            Accept: "application/json",
          },
        },
      );
      await handleApiResponse(response, navigate);
      const data = await response.json();
      if (Array.isArray(data.value)) {
        const suggestions = data.value.map((item: any) => {
          holderMap.current[item.databaseId] = item;
          return {
            label: item.name || item.companyName || item.databaseId,
            value: item.databaseId,
          };
        });
        setHolderSuggestions(suggestions);
      } else {
        setHolderSuggestions([]);
      }
    } catch {
      setHolderSuggestions([]);
    } finally {
      setLoadingHolder(false);
    }
  };

  const handleSave = async () => {
    const interestTypeBooleans = INTEREST_TYPES.reduce(
      (acc, type) => {
        acc[type.key] = interestTypes.includes(type.key);
        return acc;
      },
      {} as Record<string, boolean>,
    );
    const certInterestMap: Record<string, string> = {
      "General liability": "generalLiability",
      "Automobile liability": "automobileLiability",
      "Umbrella liability": "umbrellaLiability",
      "Workers' Compensation": "workersCompensations",
      "Other liability 1": "otherLiability",
      "Other liability 2": "otherLiability2",
      "Garage liability": "garageLiability",
      "Garage keepers liability": "garageKeepersLiability",
      Cargo: "cargo",
      "Trailer interchange": "trailerInterchange",
      "Airport FBO": "airportFBO",
      "Private hangar": "privateHangar",
      "Aviation products": "aviationProducts",
      Aircraft: "aircraft",
      "Vehicle or equipment": "vehicleOrEquipment",
      "Insurance binder": "insuranceBinder",
    };
    const certInterestSubrMap: Record<string, string> = {
      "General liability": "generalLiabilitySubrWvd",
      "Automobile liability": "automobileLiabilitySubrWvd",
      "Umbrella liability": "umbrellaLiabilitySubrWvd",
      "Workers' Compensation": "workersCompensationsSubrWvd",
      "Other liability 1": "otherLiabilitySubrWvd",
      "Other liability 2": "otherLiability2SubrWvd",
      "Garage liability": "garageLiabilitySubrWvd",
      "Garage keepers liability": "garageKeepersLiabilitySubrWvd",
      Cargo: "cargoSubrWvd",
      "Trailer interchange": "trailerInterchangeSubrWvd",
      "Airport FBO": "airportFBOSubrWvd",
      "Private hangar": "privateHangarSubrWvd",
      "Aviation products": "aviationProductsSubrWvd",
      Aircraft: "aircraftSubrWvd",
    };
    const certInterestFields: Record<string, boolean> = {};
    certInterest.forEach((row) => {
      if (certInterestMap[row.type]) {
        certInterestFields[certInterestMap[row.type]] = !!row.addl;
      }
      if (certInterestSubrMap[row.type]) {
        certInterestFields[certInterestSubrMap[row.type]] = !!row.subr;
      }
    });

    let policiesArr: string[] | undefined = undefined;
    if (policies) {
      policiesArr = [policies];
    }

    const payload: any = {
      certificateHolderName: companyName,
      email,
      cellPhone,
      phone,
      fax,
      addressLine1: address1,
      addressLine2: address2,
      city,
      zipCode: zip,
      stateAbbreviationCode: state,
      ...interestTypeBooleans,
      additionalInterestOtherText: otherText,
      loanNumber,
      description: descText,
      accord101: acord101,
      ...certInterestFields,
      ...(insuredDatabaseId ? { insuredDatabaseId } : {}),
      ...(insuredEmail ? { insuredEmail } : {}),
      ...(insuredFirstName ? { insuredFirstName } : {}),
      ...(insuredLastName ? { insuredLastName } : {}),
      ...(insuredCommercialName ? { insuredCommercialName } : {}),
      ...(policiesArr ? { policies: policiesArr } : {}),
    };
    if (selectedHolder && selectedHolder.value) {
      payload.certificateHolderDatabaseId = selectedHolder.value;
    }
    try {
      const NCToken = localStorage.getItem("NCToken");
      const igtoken = localStorage.getItem("igtoken");
      const response = await fetch(
        `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/add-additional-insured`,
        {
          method: "POST",
          headers: {
            NCToken: NCToken || "",
            Authorization: `Bearer ${igtoken || ""}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      await handleApiResponse(response, navigate);
      if (!response.ok) {
        return;
      }
      const result = await response.json();
      onSave(result);
      onHide();
    } catch (err) {
    }
  };

  return (
    <Dialog
      header={<span style={{ color: '#ba170b', fontWeight: 600 }}>Add Additional Interest</span>}
      visible={visible}
      style={{ width: "900px", maxWidth: "98vw" }}
      onHide={onHide}
      modal
      className="addCertHolderDialog"
    >
      {/* PREFILL INFORMATION */}
      <div className="pageCard" style={{ marginBottom: 12, padding: 16 }}>
        <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>
          Search list of existing holders:
        </div>
        <AutoComplete
          value={selectedHolder}
          suggestions={holderSuggestions}
          completeMethod={completeHolder}
          onChange={(e) => {
            setSelectedHolder(e.value);
            if (e.value && holderMap.current[e.value.value]) {
              const holder = holderMap.current[e.value.value];
              setCompanyName(holder.name || "");
              setEmail(holder.eMail || "");
              setCellPhone(formatUsPhone(holder.cellPhone || ""));
              setPhone(formatUsPhone(holder.phone || ""));
              setFax(formatUsPhone(holder.fax || ""));
              setAddress1(holder.addressLine1 || "");
              setAddress2(holder.addressLine2 || "");
              setCity(holder.city || "");
              setState(holder.stateName || "");
              setZip(holder.zipCode || "");
            }
          }}
          placeholder="Please select certificate holder"
          field="label"
          style={{ width: 300 }}
          inputStyle={{ width: 300 }}
          itemTemplate={(item) => (item ? <div>{item.label}</div> : null)}
        />
      </div>
      <div
        className="page__twoCol"
        style={{ gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 12 }}
      >
        <div className="pageCard" style={{ padding: 16 }}>
          <div style={{ color: '#0057A4', fontWeight: 500 }}>Company Name <span style={{ color: '#ba170b' }}>*</span></div>
          <InputText maxLength={100}
            value={companyName}
            placeholder="Please enter company name"
            onChange={(e) => setCompanyName(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#0057A4', fontWeight: 500 }}>E-mail Address <span style={{ color: '#ba170b' }}>*</span></div>
          <InputText maxLength={100}
            value={email}
            placeholder="Please enter email"
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#0057A4', fontWeight: 500 }}>Cell Phone <span style={{ color: '#ba170b' }}>*</span></div>
          <InputText maxLength={20}
            value={cellPhone}
            placeholder="Please enter cell phone"
            onChange={(e) => setCellPhone(maskUsPhone(e.target.value))}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#0057A4', fontWeight: 500 }}>Phone <span style={{ color: '#ba170b' }}>*</span></div>
          <InputText maxLength={20}
            value={phone}
            placeholder="Please enter phone"
            onChange={(e) => setPhone(maskUsPhone(e.target.value))}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#0057A4', fontWeight: 500 }}>Fax <span style={{ color: '#ba170b' }}>*</span></div>
          <InputText maxLength={20}
            value={fax}
            placeholder="Please enter fax"
            onChange={(e) => setFax(maskUsPhone(e.target.value))}
            style={{ width: "100%", marginBottom: 16 }}
          />
        </div>
        <div className="pageCard" style={{ padding: 16 }}>
          <div style={{ color: '#0057A4', fontWeight: 500 }}>Address Line 1 <span style={{ color: '#ba170b' }}>*</span></div>
          <InputText maxLength={100}
            ref={address1Ref}
            value={address1}
            placeholder="Please enter address line 1"
            onChange={(e) => handleAddress1Change(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          {addressLoading && <div style={{ fontSize: '12px', color: '#666' }}>Loading suggestions...</div>}
          {addressSuggestions.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, border: '1px solid #ccc', maxHeight: '200px', overflowY: 'auto', background: 'white', position: 'absolute', zIndex: 1000, width: '100%' }}>
              {addressSuggestions.map((suggestion, index) => (
                <li
                  key={index}
                  onClick={() => selectAddressSuggestion(suggestion)}
                  style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          )}
          <div style={{ color: '#0057A4', fontWeight: 500 }}>Address Line 2</div>
          <InputText maxLength={100}
            ref={address2Ref}
            value={address2}
            placeholder="Please enter address line 2"
            onChange={(e) => setAddress2(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#0057A4', fontWeight: 500 }}>City <span style={{ color: '#ba170b' }}>*</span></div>
          <InputText maxLength={100}
            value={city}
            placeholder="Please enter city"
            onChange={(e) => setCity(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#0057A4', fontWeight: 500 }}>State <span style={{ color: '#ba170b' }}>*</span></div>
          <InputText maxLength={50}
            value={state}
            placeholder="Please enter state"
            onChange={(e) => setState(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#0057A4', fontWeight: 500 }}>ZIP/Postal Code <span style={{ color: '#ba170b' }}>*</span></div>
          <InputText maxLength={20}
            value={zip}
            placeholder="Please enter ZIP/postal code"
            onChange={(e) => setZip(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
        </div>
      </div>
      <div className="pageCard" style={{ marginBottom: 12, padding: 16 }}>
        <Checkbox
          inputId="doNotDisplay"
          checked={doNotDisplay}
          onChange={(e) => setDoNotDisplay(e.checked ?? false)}
        />
        <label
          htmlFor="doNotDisplay"
          style={{ color: '#0057A4', fontWeight: 500, marginLeft: 8 }}
        >
          Do Not Display in Insured's List of Holders
        </label>
      </div>

      <div className="pageCard" style={{ marginBottom: 12, padding: 16 }}>
        <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>
          Additional Interest
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 18,
            marginBottom: 8,
          }}
        >
          {INTEREST_TYPES.map((type) => (
            <div
              key={type.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                minWidth: 180,
              }}
            >
              <Checkbox
                inputId={type.key}
                checked={interestTypes.includes(type.key)}
                onChange={(e) => {
                  if (e.checked) setInterestTypes([...interestTypes, type.key]);
                  else
                    setInterestTypes(
                      interestTypes.filter((t) => t !== type.key),
                    );
                }}
              />
              <label htmlFor={type.key} style={{ color: '#0057A4', fontWeight: 500 }}>
                {type.label}
              </label>
              {type.key === "other" && interestTypes.includes("other") && (
                <InputText maxLength={100}
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Other..."
                  style={{ width: 120, marginLeft: 4 }}
                />
              )}
            </div>
          ))}
        </div>
        <div style={{ color: '#0057A4', fontWeight: 500 }}>Loan Number</div>
        <InputText maxLength={30}
          value={loanNumber}
          onChange={(e) => setLoanNumber(e.target.value)}
          style={{ width: 200, marginBottom: 16 }}
        />
        {/* Grouped dropdowns: Policies+Buildings+Acord and Prof. Liability+Desc. of Operations */}
        <div className="addCertHolderDialog__row2">
          <div className="addCertHolderDialog__col">
            <div style={{ color: '#0057A4', fontWeight: 500 }}>Policies</div>
            <Dropdown
              value={policies}
              options={policiesOptions}
              onChange={e => setPolicies(e.value)}
              placeholder={policiesLoading ? "Loading..." : "Please select policy"}
              className="addCertHolderDialog__dropdown"
              loading={policiesLoading}
              emptyMessage="No available options"
            />
            <div style={{ color: '#0057A4', fontWeight: 500 }}>Buildings</div>
            <Dropdown
              value={buildings}
              options={buildingsOptions}
              onChange={e => setBuildings(e.value)}
              placeholder={buildingsLoading ? "Loading..." : "Please select building"}
              className="addCertHolderDialog__dropdown"
              loading={buildingsLoading}
              emptyMessage="No available options"
            />
            <div style={{ color: '#0057A4', fontWeight: 500 }}>Acord 101</div>
            <InputTextarea maxLength={2000}
              value={acord101}
              onChange={e => setAcord101(e.target.value)}
              rows={3}
              placeholder="Acord 101"
              className="addCertHolderDialog__textarea"
            />
          </div>
          <div className="addCertHolderDialog__col">
            <div style={{ color: '#0057A4', fontWeight: 500 }}>Professional Liability</div>
            <Dropdown
              value={profLiability}
              options={profLiabilityOptions}
              onChange={e => setProfLiability(e.value)}
              placeholder={profLiabilityLoading ? "Loading..." : "Please select professional liability"}
              className="addCertHolderDialog__dropdown"
              loading={profLiabilityLoading}
              emptyMessage="No available options"
            />
            <div style={{ color: '#0057A4', fontWeight: 500, marginTop: 8 }}>Description of Operations</div>
            <Dropdown
              value={descTemplate}
              options={descTemplateOptions}
              onChange={e => {
                setDescTemplate(e.value);
                const tmpl = descTemplateOptions.find(t => t.value === e.value);
                if (tmpl) setDescText(tmpl.body);
              }}
              placeholder={descTemplatesLoading ? 'Loading...' : 'Select template or'}
              className="addCertHolderDialog__dropdown"
              loading={descTemplatesLoading}
              emptyMessage="No available options"
            />
            <InputTextarea maxLength={2000}
              value={descText}
              onChange={e => setDescText(e.target.value)}
              rows={3}
              placeholder="Description of operations"
              className="addCertHolderDialog__textarea"
            />
          </div>
        </div>
      </div>
      <div className="pageCard" style={{ marginBottom: 12, padding: 16 }}>
        <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>
          Additional Interest for Certificates
        </div>
        <div style={{ overflowX: "auto" }}>
          <table
            className="p-datatable-table"
            style={{
              width: "100%",
              minWidth: 600,
              borderCollapse: "separate",
              borderSpacing: "0 8px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{ width: 90, textAlign: "center", padding: "6px 0" }}
                >
                  ADDL INSRD
                </th>
                <th
                  style={{ width: 90, textAlign: "center", padding: "6px 0" }}
                >
                  SUBR WVD
                </th>
                <th style={{ textAlign: "left", padding: "6px 8px" }}>
                  Type of insurance
                </th>
              </tr>
            </thead>
            <tbody>
              {certInterest.map((row, idx) => (
                <tr
                  key={row.type}
                  style={{
                    background: "#fff",
                    borderRadius: 8,
                    boxShadow: "0 1px 4px #e6e7ef44",
                  }}
                >
                  <td style={{ textAlign: "center", padding: "4px 0" }}>
                    <Checkbox
                      checked={row.addl}
                      onChange={(e) => {
                        const updated = [...certInterest];
                        updated[idx] = { ...row, addl: e.checked };
                        setCertInterest(updated);
                      }}
                      style={{ verticalAlign: "middle" }}
                    />
                  </td>
                  <td style={{ textAlign: "center", padding: "4px 0" }}>
                    <Checkbox
                      checked={row.subr}
                      onChange={(e) => {
                        const updated = [...certInterest];
                        updated[idx] = { ...row, subr: e.checked };
                        setCertInterest(updated);
                      }}
                      style={{ verticalAlign: "middle" }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "6px 10px",
                      fontWeight: 450,
                      color: '#0057A4',
                      fontSize: 15,
                    }}
                  >
                    {row.type}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 16,
        }}
      >
        <Button
          label="Cancel"
          className="p-button-text"
          onClick={onHide}
          style={{ color: '#002A5C', fontWeight: 600 }}
        />
        <Button
          label="Save"
          className="p-button-primary"
          onClick={handleSave}
          style={{ background: '#0057A4', borderColor: '#0057A4', color: '#fff', fontWeight: 600 }}
        />
      </div>
    </Dialog>
  );
}
